"use strict";

exports.__esModule = true;
exports.getTimeAggregateColumns = getTimeAggregateColumns;
exports.pivotByTime = void 0;
var _moment = _interopRequireDefault(require("moment"));
var _rgbaConvert = _interopRequireDefault(require("rgba-convert"));
var _d3Scale = require("d3-scale");
var _timestampFormatter = require("../DataTable/Formatters/timestampFormatter");
var _changeColumnUtils = require("./changeColumnUtils");
var _Metrics = require("../../../Catalogs/Metrics");
var _sortEventsByWorkdayStart = require("../common/utils/sortEventsByWorkdayStart");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
var metricNameToColorUnitsMap = _Metrics.metricDefinitions.reduce(function (acc, metric) {
  acc[metric.name + ".units"] = metric.units;
  acc[metric.name + ".color"] = metric.color;
  return acc;
}, {});
var getDocCount = function getDocCount(currentDocCount, rowValue, excludeZeroValues) {
  if (!excludeZeroValues || excludeZeroValues && rowValue) {
    return currentDocCount + 1;
  }
  return currentDocCount;
};
var pivotByTime = exports.pivotByTime = function pivotByTime(_ref) {
  var metrics = _ref.metrics,
    timeAggregateField = _ref.timeAggregateField,
    columns = _ref.columns,
    _ref$excludeZeroValue = _ref.excludeZeroValues,
    excludeZeroValues = _ref$excludeZeroValue === void 0 ? true : _ref$excludeZeroValue;
  return function (data) {
    if (data.length === 0) {
      return [];
    }
    var columnIdToPropsMap = columns.reduce(function (acc, col) {
      acc[col.id] = col;
      return acc;
    }, {});
    var result = [];
    var uniqueTimeColsMap = {};
    var metricToMinMaxMap = {};

    // For every unique tuple, once you get an entry in result, you'll only be appending more columns to it
    var uniqueTupleToResultIndexMap = {};

    // Selecting all columns other than metrics columns and time aggregate column
    var columnsToPivot = Object.keys(data[0]).filter(function (key) {
      return !metrics.includes(key) && key !== timeAggregateField;
    });

    // For every tuple excluding metric columns and time aggregate column, I will get x rows and y columns
    // where x is the number of metrics configured and y is the number of unique time aggregate values for that tuple

    var dates = new Set();
    data.forEach(function (row) {
      if (row[timeAggregateField] != null) {
        dates.add(row[timeAggregateField]);
      }
      metrics.forEach(function (metric) {
        var rowValuesPerMetricWithoutTimeAgg = columnsToPivot.map(function (key) {
          return row[key];
        }).concat(metric);
        // columns to pivot plus one metric is considered as one unique tuple
        var uniqueTuple = rowValuesPerMetricWithoutTimeAgg.join(",");

        // for above unique tuple, we will keep adding columns as and when we see a new value in time aggregate column
        if (uniqueTupleToResultIndexMap[uniqueTuple] !== undefined) {
          var _Object$assign;
          var prevTuple = result[uniqueTupleToResultIndexMap[uniqueTuple]];
          var rowValue = row[metric] ? row[metric] : 0;
          var updatedDocCount = getDocCount(prevTuple.uTupleProps.docCount, rowValue, excludeZeroValues);
          var valueArray = [].concat(prevTuple.valueArray, [rowValue]);
          var newMax = Math.max(metricToMinMaxMap[metric + ".max"], rowValue);
          var newMin = Math.min(metricToMinMaxMap[metric + ".min"], rowValue);
          metricToMinMaxMap[metric + ".max"] = newMax;
          metricToMinMaxMap[metric + ".min"] = newMin;
          uniqueTimeColsMap[row[timeAggregateField]] = true;
          result[uniqueTupleToResultIndexMap[uniqueTuple]] = Object.assign({}, prevTuple, (_Object$assign = {}, _Object$assign[row[timeAggregateField]] = rowValue, _Object$assign[row[timeAggregateField] + ".type"] = columnIdToPropsMap[metric].type, _Object$assign.uTupleProps = {
            docCount: updatedDocCount
          }, _Object$assign.valueArray = valueArray, _Object$assign));
        } else {
          var _Object$assign2;
          uniqueTupleToResultIndexMap[uniqueTuple] = result.length; // this will give a new number for every unique tuple

          var pivotColumnsValues = columnsToPivot.reduce(function (acc, key) {
            acc[key] = row[key];
            return acc;
          }, {});
          var _rowValue = row[metric] ? row[metric] : 0;
          var previousMin = metricToMinMaxMap[metric + ".min"] ? metricToMinMaxMap[metric + ".min"] : 0;
          var previousMax = metricToMinMaxMap[metric + ".max"] ? metricToMinMaxMap[metric + ".max"] : 0;
          metricToMinMaxMap[metric + ".max"] = Math.max(_rowValue, previousMax);
          metricToMinMaxMap[metric + ".min"] = Math.min(_rowValue, previousMin);
          var _valueArray = [_rowValue];
          uniqueTimeColsMap[row[timeAggregateField]] = true;
          result[uniqueTupleToResultIndexMap[uniqueTuple]] = Object.assign({}, pivotColumnsValues, (_Object$assign2 = {
            // setting pivot cols
            metric: metric
          }, _Object$assign2[row[timeAggregateField]] = _rowValue, _Object$assign2[row[timeAggregateField] + ".type"] = columnIdToPropsMap[metric].type, _Object$assign2.uTupleProps = {
            docCount: getDocCount(0, _rowValue, excludeZeroValues)
          }, _Object$assign2.valueArray = _valueArray, _Object$assign2));
        }
      });
    });
    var uniqueTimeCols = Object.keys(uniqueTimeColsMap);

    // $FlowIgnore
    return result.map(function (row) {
      var hasNonZeroValue = row.valueArray.find(function (x) {
        return x !== 0;
      });
      var total = row.valueArray.reduce(function (acc, x) {
        return acc + x;
      }, 0);
      var rowType = columnIdToPropsMap[row.metric].type;
      var totalDividedByDocCount = hasNonZeroValue ? total / row.uTupleProps.docCount : 0;
      var average = rowType === "fractionalPercent" ? parseFloat(totalDividedByDocCount).toFixed(2) : Math.floor(totalDividedByDocCount);
      var metricMin = metricToMinMaxMap[row.metric + ".min"];
      var metricMax = metricToMinMaxMap[row.metric + ".max"];
      var missingTimeCols = uniqueTimeCols.filter(function (x) {
        return !Object.keys(row).includes(x);
      });
      var fillersForMissingTimeCols = {};
      missingTimeCols.forEach(function (col) {
        fillersForMissingTimeCols[col] = 0;
        fillersForMissingTimeCols[col + ".type"] = rowType;
      });
      return Object.assign({}, row, {
        datesArray: Array.from(dates)
      }, fillersForMissingTimeCols, {
        average: average,
        total: total,
        "average.type": rowType,
        "total.type": rowType,
        "target.value.type": rowType,
        change: (0, _changeColumnUtils.getChange)(row.valueArray, metricNameToColorUnitsMap[row.metric + ".units"]),
        trendlineColor: metricNameToColorUnitsMap[row.metric + ".color"],
        opacityFn: function opacityFn(x) {
          return (0, _d3Scale.scaleLinear)().domain([metricMin, metricMax]).range([0, 1])(x);
        }
        // setting for every row even though it's enough to do per metric
        // or can set global and make getTimeAggregateColumns use that
        // or need to return it cleanly and pass it to getTimeAggregateColumns
      });
    });
  };
};
function getTimeAggregateColumns(_ref2) {
  var timeAggregateField = _ref2.timeAggregateField,
    columns = _ref2.columns,
    metrics = _ref2.metrics,
    data = _ref2.data,
    timeAggregateFieldGrouping = _ref2.timeAggregateFieldGrouping,
    showAverageColumn = _ref2.showAverageColumn,
    showTotalColumn = _ref2.showTotalColumn,
    showTrendColumn = _ref2.showTrendColumn,
    showHeatMap = _ref2.showHeatMap,
    showTargets = _ref2.showTargets,
    showChangeColumn = _ref2.showChangeColumn,
    time = _ref2.time;
  var timeAggregateColumns = [];
  var isISOTime = function isISOTime(key) {
    return (0, _moment["default"])(key, _moment["default"].ISO_8601).isValid();
  };
  var isInteger = function isInteger(key) {
    return Number.isInteger(Number(key));
  };

  // Ensuring that all rows have all columns inside pivotByTime, hence taking first row
  if (data && data.length) {
    timeAggregateColumns = Object.keys(data[0]).filter(function (key) {
      return isISOTime(key) || isInteger(key);
    }).sort(function (a, b) {
      return isISOTime(a) ? (0, _moment["default"])(a).valueOf() - (0, _moment["default"])(b).valueOf() : a - b;
    });
  }

  // Hour of day columns start at the plant's workday start hour, like the charts do
  if (timeAggregateFieldGrouping === "hour" && time) {
    timeAggregateColumns = (0, _sortEventsByWorkdayStart.sortEventsByWorkdayStart)(timeAggregateColumns.map(function (key) {
      return {
        key: key
      };
    }), time.timezone.name, time.currentWorkday.interval.startMillis).map(function (_ref3) {
      var key = _ref3.key;
      return key;
    });
  }

  // Non-metric Non-time columns
  // $FlowIgnore
  var columnsToAdd = columns.filter(function (column) {
    return column.section !== "metrics" && column.id !== timeAggregateField;
  });

  // Adding metric column
  columnsToAdd.push({
    id: "metric",
    type: "metric",
    name: "Metric",
    canSearch: true,
    canFilter: true,
    enableSorting: true,
    sortIndex: "metric"
  });
  if (showChangeColumn) {
    columnsToAdd.push({
      id: "change",
      name: "Change",
      type: "changeColumn"
    });
  }
  if (showAverageColumn) {
    columnsToAdd.push({
      id: "average",
      name: "Average",
      type: "lookUpForTypeInRow",
      canFilter: false,
      enableSorting: true,
      canSearch: false,
      sortIndex: "average"
    });
  }
  if (showTotalColumn) {
    columnsToAdd.push({
      id: "total",
      name: "Total",
      type: "lookUpForTypeInRow",
      canFilter: false,
      enableSorting: true,
      canSearch: false,
      sortIndex: "total"
    });
  }
  if (showTrendColumn) {
    columnsToAdd.push({
      id: "trend",
      name: "Trend",
      type: "trendline",
      canFilter: false,
      canSearch: false,
      sortIndex: "trend"
    });
  }
  if (showTargets) {
    columnsToAdd.push({
      id: "targetName",
      name: "Target name",
      type: "metric",
      canFilter: true,
      enableSorting: true,
      canSearch: true,
      sortIndex: "targetName"
    });
    columnsToAdd.push({
      id: "target.value",
      name: "Target value",
      type: "lookUpForTypeInRow",
      canFilter: false,
      canSearch: false,
      sortIndex: "target.value"
    });
  }

  // Add one column for every time aggregate value
  timeAggregateColumns.forEach(function (timeColumn) {
    columnsToAdd.push({
      id: timeColumn,
      type: "lookUpForTypeInRow",
      name: (0, _timestampFormatter.getFormattedDateBasedOnGrouping)(timeColumn, timeAggregateFieldGrouping),
      // need to format this
      canFilter: false,
      enableSorting: true,
      canSearch: false,
      sortIndex: timeColumn,
      getProps: function getProps() {
        for (var _len = arguments.length, props = new Array(_len), _key = 0; _key < _len; _key++) {
          props[_key] = arguments[_key];
        }
        var _ = props[0],
          row = props[1],
          column = props[2];
        if (showHeatMap && row) {
          var cellValue = row.original[column.id] || 0;
          var opacity = row.original.opacityFn(cellValue);
          var metricColor = metricNameToColorUnitsMap[row.original.metric + ".color"];
          var i = _rgbaConvert["default"].obj(metricColor);
          return {
            style: {
              background: "rgba(" + i.r + ", " + i.g + ", " + i.b + ", " + opacity + ")"
            }
          };
        }
        return {};
      }
    });
  });
  return columnsToAdd;
}